import { useState, useEffect, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { validatePasswordResetToken, resetPassword } from '~/services/account';
import { useToasts } from 'react-toast-notifications';
import { PasswordInput, TextInput } from '~/shared/components/form/Inputs';
import { Container, Button, Card, CardBody, Col, Row, Form } from 'reactstrap';
import { Loading } from '../../shared/components/Loading';
import { AuthContext } from '~/context/auth';

type FormData = {
    email: string;
    password: string;
    password_confirmation: string;
};

// Get admin URL from environment and remove trailing '/api' if present
// @ts-ignore - Ignore TS errors with import.meta for Vite environment variables
const ADMIN_URL = import.meta.env?.VITE_NEW_ADMIN_URL || '';

export const ResetPassword = ({}: any) => {
    const [{ user }, userDispatch] = useContext(AuthContext);
    const [loading, setLoading] = useState<boolean>(true);
    const [saving, setSaving] = useState<boolean>(false);
    const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
    const [tokenEmail, setTokenEmail] = useState<string>('');
    const search = window.location.search;
    const params = new URLSearchParams(search);
    const token = params.get('token');
    const { addToast } = useToasts();
    const history = useHistory();
    const {
        handleSubmit,
        control,
        watch,
        formState: { isValid },
    } = useForm<FormData>({
        defaultValues: {
            email: '',
            password: '',
            password_confirmation: '',
        },
        mode: 'onChange',
    });

    const validateToken = async () => {
        if (token) {
            const resp = await validatePasswordResetToken(token);
            if (resp.detail === 'Not found.') {
                addToast('Invalid token.', { appearance: 'error' });
                history.push('/forgot-password');
            }
            if (resp?.email) {
                setTokenEmail(resp.email);
            }
            setLoading(false);
        }
    };

    useEffect(() => {
        // Clear authentication token and user when visiting password reset page
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        
        // Clear user from context
        userDispatch({
            type: 'set_user',
            user: null,
        });

        validateToken();
    }, [token]);

    const onSubmit = async (data: FormData) => {
        setSaving(true);
        const resp = await resetPassword(token, data.password);
        if (!resp.user) {
            addToast('Something went wrong', { appearance: 'error' });
        } else {
            // Reset localStorage
            localStorage.setItem('token', resp.token);
            localStorage.setItem(
                'user',
                JSON.stringify({
                    username: resp.user.username,
                    password: '',
                })
            );
            userDispatch({
                type: 'set_user',
                payload: resp.user,
            });
            addToast('Password reset!', { appearance: 'success' });

            // Check if user is staff or superuser and redirect accordingly
            if (resp.user.is_staff || resp.user.is_superuser) {
                window.location.href = ADMIN_URL || '/';
            } else {
                // Regular user redirect to dashboard
                history.push('/dashboard');
            }
        }
    };

    const CreatePasswordForm = () => {
        return (
            <>
                <h3 className='mb-5 text-start'>Reset Password</h3>
                <p className='text-start'>
                    If your password contains capital letters, include the
                    capital letters each time you type the password.
                </p>
                <Form onSubmit={handleSubmit(onSubmit)}>
                    <TextInput
                        name='email'
                        label='Confirm Your Email'
                        type='email'
                        control={control}
                        required={true}
                        ariaRequired={true}
                        maxLength={255}
                        newRules={{
                            validate: (value: string) => {
                                if (!tokenEmail) {
                                    return true;
                                }
                                const normalized = value
                                    ?.trim()
                                    .toLowerCase();
                                return (
                                    normalized ===
                                        tokenEmail.trim().toLowerCase() ||
                                    'Email must match the address on your reset link'
                                );
                            },
                        }}
                    />
                    <PasswordInput
                        name='password'
                        label='New Password'
                        control={control}
                        required={true}
                        ariaRequired={true}
                        helperText='Must contain at least 8 characters'
                    />
                    <PasswordInput
                        name='password_confirmation'
                        label='Confirm New Password'
                        control={control}
                        required={true}
                        ariaRequired={true}
                        validate={(value: string) => {
                            const password = watch('password');
                            return (
                                value === password ||
                                'Passwords must match'
                            );
                        }}
                    />
                    <br />
                    <Button
                        className='me-0'
                        color='primary'
                        type='submit'
                        disabled={saving || !isValid}
                        aria-label='Save new password'
                    >
                        {saving ? `Saving` : `Reset Password`}
                    </Button>
                    <br />
                    <br />
                    <Button
                        color='link'
                        onClick={() => history.push('/')}
                        aria-label='Navigate back to login page'
                    >
                        Return to Login
                    </Button>
                </Form>
            </>
        );
    };

    const Confirmation = () => {
        return (
            <div>
                <h3 className='mb-5'>Password Reset!</h3>
                <p>Please check your email to take action.</p>
                <Row>
                    <Button
                        className='btn btn-text'
                        onClick={() => history.push('/')}
                        aria-label='Navigate back to login page'
                    >
                        Return to Login
                    </Button>
                </Row>
            </div>
        );
    };

    if (loading) {
        return <Loading />;
    }

    return (
        <div className='vh-100 d-flex flex-column justify-content-center'>
            <div>
                <a
                    className='sidebar__logo mx-auto mb-5'
                    style={{ width: '316px', height: '120px' }}
                    href={import.meta.env.VITE_WP_URL}
                />
            </div>
            <div>
                <Container className='w-100' style={{ maxWidth: '960px' }}>
                    <Row className='justify-content-center'>
                        <Col md='6'>
                            <Card className='border border-primary'>
                                <CardBody className='p-5 text-center'>
                                    {!showConfirmation ? (
                                        <CreatePasswordForm />
                                    ) : (
                                        <Confirmation />
                                    )}
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        </div>
    );
};
